import { request } from "../client";

import type {
  DictationSessionRequest,
  DictationSessionResponse,
  DictationSubmissionRequest,
  DictationResult,
  DictationStats,
  DictationHistoryItem,
  DictationSettings,
  DictationEligibilityResponse,
} from "../types/dictation";
import type {
  DictationRecommendation,
  DictationRecommendationRequest,
} from "@lingoshade/domain";

export function createDictationEndpoints(baseUrl: string): {
  getRecommendation: (
    options?: DictationRecommendationRequest,
    token?: string | null,
  ) => Promise<DictationRecommendation | null>;
  start: (
    req?: DictationSessionRequest,
    token?: string | null,
  ) => Promise<DictationSessionResponse>;
  submit: (
    req: DictationSubmissionRequest,
    token?: string | null,
  ) => Promise<DictationResult>;
  getStats: (token?: string | null) => Promise<DictationStats>;
  getHistory: (
    limit?: number,
    offset?: number,
    token?: string | null,
  ) => Promise<DictationHistoryItem[]>;
  getSettings: (token?: string | null) => Promise<DictationSettings>;
  updateSettings: (
    settings: Partial<DictationSettings>,
    token?: string | null,
  ) => Promise<{ success: boolean }>;
  getSessionDetails: (
    sessionId: string,
    token?: string | null,
  ) => Promise<
    DictationResult & {
      session: DictationSessionResponse;
      textContent: string;
    }
  >;
  checkEligibility: (
    token?: string | null,
  ) => Promise<DictationEligibilityResponse>;
} {
  return {
    getRecommendation(
      options?: DictationRecommendationRequest,
      token?: string | null,
    ): Promise<DictationRecommendation | null> {
      return request<DictationRecommendation | null>(
        baseUrl,
        "/dictation/recommendation",
        {
          method: "GET",
          query: {
            excludeTextId: options?.excludeTextId,
            textSize: options?.textSize,
          },
          token,
        },
      );
    },
    /**
     * Начать новую сессию диктанта
     */
    start(
      req: DictationSessionRequest = {},
      token?: string | null,
    ): Promise<DictationSessionResponse> {
      return request<DictationSessionResponse>(baseUrl, "/dictation/start", {
        method: "POST",
        body: req,
        token,
      });
    },

    /**
     * Отправить результат диктанта
     */
    submit(
      req: DictationSubmissionRequest,
      token?: string | null,
    ): Promise<DictationResult> {
      return request<DictationResult>(baseUrl, "/dictation/submit", {
        method: "POST",
        body: req,
        token,
      });
    },

    /**
     * Получить статистику диктантов пользователя
     */
    getStats(token?: string | null): Promise<DictationStats> {
      return request<DictationStats>(baseUrl, "/dictation/stats", {
        method: "GET",
        token,
      });
    },

    /**
     * Получить историю диктантов
     */
    getHistory(
      limit = 20,
      offset = 0,
      token?: string | null,
    ): Promise<DictationHistoryItem[]> {
      return request<DictationHistoryItem[]>(baseUrl, "/dictation/history", {
        method: "GET",
        query: { limit, offset },
        token,
      });
    },

    /**
     * Получить настройки диктанта пользователя
     */
    getSettings(token?: string | null): Promise<DictationSettings> {
      return request<DictationSettings>(baseUrl, "/dictation/settings", {
        method: "GET",
        token,
      });
    },

    /**
     * Обновить настройки диктанта
     */
    updateSettings(
      settings: Partial<DictationSettings>,
      token?: string | null,
    ): Promise<{ success: boolean }> {
      return request<{ success: boolean }>(baseUrl, "/dictation/settings", {
        method: "PUT",
        body: settings,
        token,
      });
    },

    /**
     * Получить детальные результаты сессии диктанта
     */
    getSessionDetails(
      sessionId: string,
      token?: string | null,
    ): Promise<
      DictationResult & {
        session: DictationSessionResponse;
        textContent: string;
      }
    > {
      return request<
        DictationResult & {
          session: DictationSessionResponse;
          textContent: string;
        }
      >(baseUrl, `/dictation/sessions/${sessionId}`, {
        method: "GET",
        token,
      });
    },

    /**
     * Check dictation eligibility/limits
     */
    checkEligibility(
      token?: string | null,
    ): Promise<DictationEligibilityResponse> {
      return request<DictationEligibilityResponse>(
        baseUrl,
        "/dictation/eligibility",
        {
          method: "GET",
          token,
        },
      );
    },
  };
}
